import React, { useState, useEffect, useRef, memo } from 'react';
import html2canvas from 'html2canvas';
import axiosInstance from '../utils/axiosConfig';
import '../css/card_dashboard.css';
import '../css/cardnews.css';
import ImageSelectionModal from './ImageSelectionModal';
import FilterSection from './FilterSection';
import JSZip from 'jszip';
import GIF from 'gif.js';
import { parseGIF, decompressFrames } from 'gifuct-js';
import { useUsage } from '../contexts/UsageContext';

// 카드 푸터 컴포넌트 - 메모이제이션으로 리렌더링 방지
const CardFooter = memo(({ approvalNumber }) => {
  const showApprovalNumber = approvalNumber && approvalNumber !== '없음' && approvalNumber !== 'N/A';
  return (
    <div className="card-footer-db">
      <img
        src="/img/logo.png"
        alt="Company Logo"
        className="company-logo-db"
        crossOrigin="anonymous"
        loading="eager"
        fetchpriority="high"
      />
      {showApprovalNumber && (
        <span className='approval-number'>심의번호 : {approvalNumber}</span>
      )}
    </div>
  );
});

const CardDashboard = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const cardRefs = useRef([]);
  const { refreshUsage } = useUsage();
  const [cards, setCards] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 페이지네이션 상태 추가
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 16,
    total: 0,
    total_pages: 0,
    has_prev: false,
    has_next: false
  });
  const [filterOptions, setFilterOptions] = useState({
    scenarios: [],
    ages: [],
    genders: [],
    months: [],
    statuses: []
  });
  const [filters, setFilters] = useState({
    scenario: 'all',
    age: 'all',
    gender: 'all',
    month: 'all',
    status: 'all'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState(''); // 입력 중인 검색어
  const [editData, setEditData] = useState({
    title: '',
    subtitle: '',
    description: '',
    status: '생성',
    approval_number: '',
    img_url: '',
    border_radius: '15px',
    font_style: 'DBGothicL',
    bg_url: 'none'
  });

  const statusOptions = ['생성', '검토완료', '심의완료', '사용불가'];
  const backgroundOptions = [
    { value: 'none', label: '없음' },
    { value: 'bg_retro_frame.png', label: '레트로 프레임' },
    { value: 'bg_emboss_leaf.png', label: '엠보스 리프' },
    { value: 'bg_korean_paper.png', label: '한지' },
    { value: 'bg_sky_star.png', label: '하늘 별' },
  ];

  const fontOptions = [
    { value: 'DBGothicL', label: 'DB Gothic Light' },
    { value: 'DBGothicM', label: 'DB Gothic Medium' },
    { value: 'DBGothicB', label: 'DB Gothic Bold' },
    { value: 'DBDreamB', label: 'DB Dream Bold' },
    { value: 'EungeulipParkDahyun', label: '은글잎 박다현체' },
    { value: 'ongleip_konkon', label: '은글잎 콘콘체체' },
    { value: 'MemomentKkukkukk', label: '모멘트 꾸꾹체' },
    { value: 'Paperlogy', label: '페이퍼로지' },
    // { value: 'PyeongChangPeace', label: '평창평화체' },
  ];

  // 이미지 재생성 및 업데이트 관련 state
  const [regeneratingImage, setRegeneratingImage] = useState(false);
  const [imageVariations, setImageVariations] = useState([]);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);

  // isImageModalOpen state 추가
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [activeCardId, setActiveCardId] = useState(null);
  const [currentCardInfo, setCurrentCardInfo] = useState(null);

  // GIF 모드 관련 state 추가
  const [activeTab, setActiveTab] = useState({}); // 각 카드의 활성 탭 상태 (일반/GIF)
  const [gifGenerating, setGifGenerating] = useState({}); // 각 카드의 GIF 생성 로딩 상태
  const [gifSuccess, setGifSuccess] = useState({}); // 각 카드의 GIF 생성 성공 상태
  const [gifQueuePosition, setGifQueuePosition] = useState({}); // 각 카드의 대기열 위치
  const [gifJobIds, setGifJobIds] = useState({}); // 각 카드의 job_id 저장
  const pollIntervalsRef = useRef({}); // 폴링 interval 참조 저장
  const pollTimeoutsRef = useRef({}); // 폴링 timeout 참조 저장

  // 단일 카드 이미지 재생성 관련 state 추가
  const [singleRegenerating, setSingleRegenerating] = useState({}); // 각 카드의 재생성 로딩 상태

  // New utility function to check text overflow
  // const isTextOverflowing = (text) => {
  //   const tempElement = document.createElement('div');
  //   tempElement.style.position = 'absolute';
  //   tempElement.style.visibility = 'hidden';
  //   tempElement.style.width = '340px'; // Match CSS width
  //   tempElement.style.fontFamily = 'DBGothicL';
  //   tempElement.style.fontSize = '18px';
  //   tempElement.style.lineHeight = '1.8';
  //   tempElement.style.WebkitLineClamp = '5';
  //   tempElement.style.display = '-webkit-box';
  //   tempElement.style.WebkitBoxOrient = 'vertical';
  //   tempElement.innerHTML = text;
  //   document.body.appendChild(tempElement);
    
  //   const isOverflowing = tempElement.scrollHeight > tempElement.clientHeight;
  //   document.body.removeChild(tempElement);
  //   return isOverflowing;
  // };

  const isTextOverflowing = (text) => {
    const maxLength = 80; // Character limit
    return text.length > maxLength;
  };

  // 폰트에 따른 스타일 계산 함수
  const getFontStyle = (fontStyle, element) => {
    const baseStyle = { fontFamily: fontStyle || 'DBGothicL' };

    // 은글잎 박다현체: letter-spacing 0px, title/subtitle +2px 크기
    if (fontStyle === 'EungeulipParkDahyun') {
      const fontSize = (element === 'title' || element === 'subtitle') ? '32px' : '22px';
      return { ...baseStyle, fontSize, letterSpacing: '0px' };
    }
    return baseStyle;
  };

  // 폴링 정리 함수
  const clearPolling = (cardId) => {
    if (pollIntervalsRef.current[cardId]) {
      clearInterval(pollIntervalsRef.current[cardId]);
      delete pollIntervalsRef.current[cardId];
    }
    if (pollTimeoutsRef.current[cardId]) {
      clearTimeout(pollTimeoutsRef.current[cardId]);
      delete pollTimeoutsRef.current[cardId];
    }
  };

  // GIF 작업 상태 폴링 함수 (card_id 기반 새 API 사용)
  const pollGifStatusByCardId = (cardId) => {
    // 이미 해당 카드에 대해 폴링 중이면 중복 방지
    if (pollIntervalsRef.current[cardId]) {
      console.log('이미 폴링 중:', cardId);
      return;
    }

    setGifGenerating(prev => ({ ...prev, [cardId]: true }));

    const pollInterval = setInterval(async () => {
      try {
        // 새 API: card_id 기반 상태 조회
        const statusRes = await axiosInstance.get(`/generate/gif/cards/${cardId}/status`);
        const statusData = statusRes.data;

        console.log('GIF 생성 상태 (폴링):', cardId, statusData.status);

        // 대기열 위치 업데이트
        if (statusData.queue_position !== undefined) {
          setGifQueuePosition(prev => ({ ...prev, [cardId]: statusData.queue_position }));
        }

        if (statusData.status === 'completed') {
          // 성공: 카드 목록 업데이트
          setCards(prev => prev.map(card =>
            card.card_id === cardId
              ? { ...card, gif_url: statusData.gif_url }
              : card
          ));

          // 성공 애니메이션 표시
          setGifSuccess(prev => ({ ...prev, [cardId]: true }));
          setGifQueuePosition(prev => ({ ...prev, [cardId]: null }));
          clearPolling(cardId);

          // 사용량 갱신
          refreshUsage();

          setTimeout(() => {
            setGifSuccess(prev => ({ ...prev, [cardId]: false }));
            setGifGenerating(prev => ({ ...prev, [cardId]: false }));
          }, 1500);

        } else if (statusData.status === 'failed') {
          // 실패: 에러 처리
          console.error('GIF 생성 실패:', statusData.error);
          setGifGenerating(prev => ({ ...prev, [cardId]: false }));
          setGifQueuePosition(prev => ({ ...prev, [cardId]: null }));
          clearPolling(cardId);
        } else if (statusData.status === 'processing') {
          // 처리 중: 대기열 위치 0
          setGifQueuePosition(prev => ({ ...prev, [cardId]: 0 }));
        } else if (statusData.status === 'none') {
          // 작업 없음: 폴링 중지
          console.log('GIF 작업 없음:', cardId);
          setGifGenerating(prev => ({ ...prev, [cardId]: false }));
          setGifQueuePosition(prev => ({ ...prev, [cardId]: null }));
          clearPolling(cardId);
        }
      } catch (pollErr) {
        console.error('상태 확인 중 오류:', pollErr);
        setGifGenerating(prev => ({ ...prev, [cardId]: false }));
        setGifQueuePosition(prev => ({ ...prev, [cardId]: null }));
        clearPolling(cardId);
      }
    }, 2000);

    // interval 참조 저장
    pollIntervalsRef.current[cardId] = pollInterval;

    // 타임아웃 설정 (10분)
    const pollTimeout = setTimeout(() => {
      console.log('GIF 생성 타임아웃:', cardId);
      setGifGenerating(prev => ({ ...prev, [cardId]: false }));
      setGifQueuePosition(prev => ({ ...prev, [cardId]: null }));
      clearPolling(cardId);
    }, 600000);

    // timeout 참조 저장
    pollTimeoutsRef.current[cardId] = pollTimeout;
  };

  // 카드의 GIF 생성 상태 확인 (페이지 로드 시 사용)
  const checkGifStatus = async (cardId) => {
    try {
      const res = await axiosInstance.get(`/generate/gif/cards/${cardId}/status`);
      const data = res.data;

      if (data.status === 'queued') {
        // 대기열에 있음 - 폴링 시작
        setGifGenerating(prev => ({ ...prev, [cardId]: true }));
        setGifQueuePosition(prev => ({ ...prev, [cardId]: data.queue_position }));
        pollGifStatusByCardId(cardId);
      } else if (data.status === 'processing') {
        // 처리 중 - 폴링 시작
        setGifGenerating(prev => ({ ...prev, [cardId]: true }));
        setGifQueuePosition(prev => ({ ...prev, [cardId]: 0 }));
        pollGifStatusByCardId(cardId);
      }
      // completed, none, failed는 폴링 불필요
    } catch (err) {
      console.error('GIF 상태 확인 실패:', cardId, err);
    }
  };

  // 단일 카드 이미지 재생성 (imageStyle 선택 가능)
  const handleSingleRegenerateImage = async (cardId, imageStyle = 'realistic') => {
    try {
      setSingleRegenerating(prev => ({ ...prev, [cardId]: true }));

      // 사용량 먼저 갱신하여 화면에 표시
      await refreshUsage();

      const response = await axiosInstance.post(`/regenerate-image/${cardId}`, {
        imageStyle: imageStyle
      });

      if (response.data.status === 200) {
        // 카드 데이터 업데이트
        setCards(prev => prev.map(card =>
          card.card_id === cardId
            ? { ...card, ...response.data.card }
            : card
        ));

        // 수정 모드인 경우 editData도 업데이트
        if (editingId === cardId) {
          setEditData(prev => ({
            ...prev,
            img_url: response.data.card.img_url
          }));
        }

        // 사용량 갱신
        refreshUsage();

        alert('이미지가 재생성되었습니다.');
      }
    } catch (error) {
      console.error('Error regenerating single image:', error);
      // 서버 에러 메시지에 따른 사용자 친화적 메시지 표시
      const errorCode = error.response?.data?.code;
      const errorMessage = error.response?.data?.message;

      if (errorCode === 'LIMIT_EXCEEDED' || errorMessage?.includes('limit exceeded')) {
        alert('오늘의 이미지 생성 할당량이 모두 소진되었습니다.\n내일 다시 시도해 주세요.');
      } else {
        alert(error.response?.data?.error || errorMessage || '이미지 재생성에 실패했습니다.');
      }
    } finally {
      setSingleRegenerating(prev => ({ ...prev, [cardId]: false }));
    }
  };

  const handleSelectVariation = (imageUrl) => {
    setSelectedVariation(imageUrl);
  };

  // 뷰포트에 보이는 이미지만 로드
  const CardImage = ({ src, alt }) => {
    const [isVisible, setIsVisible] = useState(false);
    const imgRef = useRef();
  
    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(imgRef.current);
          }
        },
        { threshold: 0.1 }
      );
  
      observer.observe(imgRef.current);
      return () => observer.disconnect();
    }, []);
  
    return (
      <div ref={imgRef} className="card-image-db">
        {isVisible ? (
          <img
            src={src}
            alt={alt}
            className="generated-image"
            crossOrigin="anonymous"
          />
        ) : (
          <div className="image-placeholder" />
        )}
      </div>
    );
  };
  
  const handleApplyVariation = async (cardId) => {
    if (!selectedVariation) return;

    try {
      await axiosInstance.post(`/update-image/${cardId}`, {
        image_url: selectedVariation
      });

      // Refresh cards data
      await fetchCards();

      // 사용량 갱신
      refreshUsage();

      // Reset states and close modal
      setImageVariations([]);
      setSelectedVariation(null);
      setCurrentImageUrl(null);
      setIsImageModalOpen(false);  // 모달 닫기 추가
    } catch (error) {
      console.error('Error updating image:', error);
      setError('이미지 업데이트에 실패했습니다.');
    }
  };
  
  const handleCancelVariation = () => {
    setImageVariations([]);
    setSelectedVariation(null);
    setCurrentImageUrl(null);
    setIsImageModalOpen(false);
    setActiveCardId(null);  // activeCardId 리셋 추가
  };

  // GIF 생성 버튼 클릭 핸들러 (Queue 시스템)
  const handleGifMode = async (cardId) => {
    try {
      console.log('GIF 생성 요청:', cardId);

       
      // 0. 사용량 먼저 갱신하여 화면에 표시
      await refreshUsage();

      // 1. GIF 생성 요청 (job_id와 queue_position 받기)
      const response = await axiosInstance.post(`/generate/gif/cards/${cardId}`, {});

      if (response.data.code === 'LIMIT_EXCEEDED' && response.data.success === false) {
        alert('오늘의 GIF 생성 할당량이 모두 소진되었습니다.\n내일 다시 시도해 주세요.');
        return;
      }

      if (!response.data || !response.data.job_id) {
        throw new Error('job_id를 받지 못했습니다.');
      }

      const { job_id, status, queue_position } = response.data;
      console.log('Job ID:', job_id, 'Status:', status, 'Queue Position:', queue_position);

      // job_id 저장 (state)
      setGifJobIds(prev => ({ ...prev, [cardId]: job_id }));

      // 초기 대기열 위치 설정
      if (status === 'queued' && queue_position) {
        setGifQueuePosition(prev => ({ ...prev, [cardId]: queue_position }));
      }

      // 2. 폴링 시작 (card_id 기반 새 API 사용)
      pollGifStatusByCardId(cardId);

    } catch (err) {
      console.error('GIF 생성 요청 실패:', err);
      // 서버 에러 메시지에 따른 사용자 친화적 메시지 표시
      const errorCode = err.response?.data?.code;
      const errorMessage = err.response?.data?.message;

      if (errorCode === 'LIMIT_EXCEEDED' || errorMessage?.includes('limit exceeded')) {
        alert('오늘의 GIF 생성 할당량이 모두 소진되었습니다.\n내일 다시 시도해 주세요.');
      } else {
        alert(err.response?.data?.error || errorMessage || 'GIF 생성 요청 중 오류가 발생했습니다.');
      }

      setGifGenerating(prev => ({ ...prev, [cardId]: false }));
      setGifQueuePosition(prev => ({ ...prev, [cardId]: null }));
    }
  };

  // 탭 전환 핸들러
  const handleTabChange = (cardId, tab) => {
    setActiveTab(prev => ({
      ...prev,
      [cardId]: tab
    }));
  };

  // 초기 로딩: 필터 옵션과 첫 페이지 데이터 가져오기
  useEffect(() => {
    fetchFilterOptions();
    fetchCards(1);
  }, []);

  // 컴포넌트 언마운트 시 모든 폴링 정리
  useEffect(() => {
    return () => {
      // 모든 interval 정리
      Object.keys(pollIntervalsRef.current).forEach(cardId => {
        clearInterval(pollIntervalsRef.current[cardId]);
      });
      // 모든 timeout 정리
      Object.keys(pollTimeoutsRef.current).forEach(cardId => {
        clearTimeout(pollTimeoutsRef.current[cardId]);
      });
    };
  }, []);

  // 필터 또는 검색어 변경 시 데이터 재조회
  useEffect(() => {
    // 초기 로딩 이후 필터/검색어 변경 시 첫 페이지로 재조회
    if (filterOptions.scenarios.length > 0) {  // 초기 로딩 완료 후에만 실행
      fetchCards(1);
    }
  }, [filters, searchQuery]);

  // 페이지 변경 핸들러
  const handlePageChange = (page) => {
    fetchCards(page);
  };

  // 필터 옵션을 서버에서 가져오는 함수 (전체 데이터 기준)
  const fetchFilterOptions = async () => {
    try {
      const response = await axiosInstance.get(`/filter-options`);
      setFilterOptions(response.data);
      // console.log("필터 옵션 로드:", response.data);
    } catch (err) {
      console.error('필터 옵션 로드 실패:', err);
      // 실패 시 기본값 유지
    }
  };

  const fetchCards = async (page = 1) => {
    try {
      setLoading(true);

      // 필터 파라미터 구성
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '16'
      });

      // 'all'이 아닌 필터만 추가
      if (filters.scenario !== 'all') params.append('scenario', filters.scenario);
      if (filters.age !== 'all') params.append('age', filters.age);
      if (filters.gender !== 'all') params.append('gender', filters.gender);
      if (filters.month !== 'all') params.append('month', filters.month);
      if (filters.status !== 'all') params.append('status', filters.status);

      // 검색어가 있으면 /cards/search 엔드포인트 사용
      let endpoint = '/cards';
      if (searchQuery.trim()) {
        params.append('keyword', searchQuery.trim());
        endpoint = '/cards/search';
      }

      const response = await axiosInstance.get(`${endpoint}?${params.toString()}`);
      const loadedCards = response.data.cards;
      setCards(loadedCards);
      setPagination(response.data.pagination);
      setCurrentPage(page);
      setError(null);

      // 로드된 카드들 중 gif_url이 없는 카드들의 GIF 생성 상태 확인
      loadedCards.forEach(card => {
        if (!card.gif_url) {
          checkGifStatus(card.card_id);
        }
      });
    } catch (err) {
      setError('카드 데이터를 불러오는데 실패했습니다.');
      console.error('Error fetching cards:', err);
    } finally {
      setLoading(false);
    }
  };

  // const filteredCards = cards.filter(card => {
  //   return (filters.scenario === 'all' || card.scenario_case === filters.scenario) &&
  //          (filters.age === 'all' || card.age === filters.age) &&
  //          (filters.gender === 'all' || card.gender === filters.gender) &&
  //          (filters.month === 'all' || card.month === filters.month) &&
  //          (filters.status === 'all' || card.status === filters.status);
  // });

  // 페이지네이션에서는 서버에서 이미 필터링된 데이터를 받으므로 클라이언트 필터링 제거
  const filteredCards = Array.isArray(cards) ? cards : [];

  const handleEdit = (card) => {
    setEditingId(card.card_id);
    setEditData({
      title: card.title,
      subtitle: card.subtitle,
      description: card.description,
      status: card.status,
      approval_number: card.approval_number,
      img_url: card.img_url,
      gif_url: card.gif_url,
      border_radius: card.border_radius || '15px',
      font_style: card.font_style || 'DBGothicL',
      bg_url: card.bg_url || 'none',
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData({
      title: '',
      subtitle: '',
      description: '',
      status: '',
      approval_number: '',
      img_url: '',
      border_radius: '15px',
      font_style: 'DBGothicL',
      bg_url: 'none',
    });
  };

  const handleUpdate = async (cardId) => {
    try {
      await axiosInstance.post(`/cards/${cardId}`, editData);
      setEditingId(null);
      await fetchCards();
      setError(null);
    } catch (err) {
      setError('카드 업데이트에 실패했습니다.');
      console.error('Error updating card:', err);
    }
  };

  const handleStatusChange = (newStatus) => {
    setEditData(prev => ({
      ...prev,
      status: newStatus
    }));
  };

  const downloadCard = async (cardElement, fileName) => {
    try {
      const canvas = await html2canvas(cardElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: true,
        imageTimeout: 0,
        // foreignObjectRendering: true // 추가
      });
      
      const link = document.createElement('a');
      link.download = fileName;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error downloading card:', error);
      alert('카드 다운로드 중 오류가 발생했습니다.');
    }
  };

  const handleDownloadSingle = async (cardId, index) => {
    const card = cards.find((c) => c.card_id === cardId);
    const cardElement = cardRefs.current[index];

    // GIF 모드 + gif_url 있을 때만 GIF로 합성
    if (activeTab[cardId] === 'gif' && card?.gif_url && cardElement) {
      try {
        // 1. 카드에서 이미지 요소 찾기
        const imgElement = cardElement.querySelector('.generated-image');
        if (!imgElement) {
          throw new Error('이미지 요소를 찾을 수 없습니다.');
        }

        const cardRect = cardElement.getBoundingClientRect();
        const imgRect = imgElement.getBoundingClientRect();

        const scale = 2; // html2canvas scale
        const imgX = (imgRect.left - cardRect.left) * scale;
        const imgY = (imgRect.top - cardRect.top) * scale;
        const imgWidth = imgRect.width * scale;
        const imgHeight = imgRect.height * scale;

        // 2. 전체 카드 캡처 (텍스트 + 로고 + 이미지 모두 포함)
        const fullCardCanvas = await html2canvas(cardElement, {
          scale,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
        });

        // 3. 전체 캡처에서 이미지 영역만 비우기 (템플릿 생성)
        const baseCanvas = document.createElement('canvas');
        baseCanvas.width = fullCardCanvas.width;
        baseCanvas.height = fullCardCanvas.height;
        const baseCtx = baseCanvas.getContext('2d');

        // 전체 카드 복사
        baseCtx.drawImage(fullCardCanvas, 0, 0);

        // 이미지 영역만 투명하게 만들기 (구멍 뚫기)
        const cardBorderRadius = parseFloat(card.border_radius) || 15;
        baseCtx.save();
        baseCtx.globalCompositeOperation = 'destination-out';

        if (cardBorderRadius >= 150) {
          // 원형
          baseCtx.beginPath();
          baseCtx.arc(
            imgX + imgWidth / 2,
            imgY + imgHeight / 2,
            imgWidth / 2,
            0,
            Math.PI * 2
          );
          baseCtx.fill();
        } else {
          // 둥근 사각형
          const radius = cardBorderRadius * scale;
          baseCtx.beginPath();
          baseCtx.moveTo(imgX + radius, imgY);
          baseCtx.lineTo(imgX + imgWidth - radius, imgY);
          baseCtx.quadraticCurveTo(imgX + imgWidth, imgY, imgX + imgWidth, imgY + radius);
          baseCtx.lineTo(imgX + imgWidth, imgY + imgHeight - radius);
          baseCtx.quadraticCurveTo(
            imgX + imgWidth,
            imgY + imgHeight,
            imgX + imgWidth - radius,
            imgY + imgHeight
          );
          baseCtx.lineTo(imgX + radius, imgY + imgHeight);
          baseCtx.quadraticCurveTo(imgX, imgY + imgHeight, imgX, imgY + imgHeight - radius);
          baseCtx.lineTo(imgX, imgY + radius);
          baseCtx.quadraticCurveTo(imgX, imgY, imgX + radius, imgY);
          baseCtx.fill();
        }

        baseCtx.restore();

        // 4. 서버 GIF 불러와서 프레임 분해
        const response = await fetch(getSecureImageUrl(card.gif_url));
        const arrayBuffer = await response.arrayBuffer();
        const gifData = parseGIF(arrayBuffer);
        const frames = decompressFrames(gifData, true);

        if (!frames || frames.length === 0) {
          throw new Error('GIF 프레임을 추출할 수 없습니다.');
        }

        // 고정 출력 해상도
        const outputWidth = 396;
        const outputHeight = 840;
        const scaleX = outputWidth / baseCanvas.width;
        const scaleY = outputHeight / baseCanvas.height;

        // 스케일된 이미지 영역 좌표 계산
        const scaledImgX = imgX * scaleX;
        const scaledImgY = imgY * scaleY;
        const scaledImgWidth = imgWidth * scaleX;
        const scaledImgHeight = imgHeight * scaleY;

        // 4. GIF 인코더 초기화 (고속 처리)
        const gif = new GIF({
          workers: 4,
          quality: 10,
          globalPalette: true,
          width: outputWidth,
          height: outputHeight,
          workerScript: '/gif.worker.js',
          dither: false,
          repeat: 0,
        });

        // 합성용 캔버스 (고정 해상도)
        const compositeCanvas = document.createElement('canvas');
        compositeCanvas.width = outputWidth;
        compositeCanvas.height = outputHeight;
        const compositeCtx = compositeCanvas.getContext('2d', { willReadFrequently: true });

        // 5. 프레임 루프 - 템플릿에 GIF 프레임 넣기
        const gifFullWidth = frames[0].dims.width;
        const gifFullHeight = frames[0].dims.height;

        // 전체 GIF를 누적 렌더링할 캔버스 (원본 GIF 크기 유지)
        const gifAccumCanvas = document.createElement('canvas');
        gifAccumCanvas.width = gifFullWidth;
        gifAccumCanvas.height = gifFullHeight;
        const gifAccumCtx = gifAccumCanvas.getContext('2d', { alpha: false, willReadFrequently: true });

        const frameSkip = 2; // 매 2프레임마다 출력

        for (let i = 0; i < frames.length; i++) {
          const frame = frames[i];

          // 모든 프레임을 누적 캔버스에 그리기
          if (i > 0 && frames[i - 1].disposalType === 2) {
            gifAccumCtx.clearRect(0, 0, gifFullWidth, gifFullHeight);
          }

          const frameImageData = gifAccumCtx.createImageData(frame.dims.width, frame.dims.height);
          frameImageData.data.set(frame.patch);
          gifAccumCtx.putImageData(frameImageData, frame.dims.left, frame.dims.top);

          // 스킵 간격마다만 GIF에 프레임 추가
          // if (i % frameSkip !== 0) continue;

          // GIF 프레임을 먼저 그리기
          compositeCtx.clearRect(0, 0, outputWidth, outputHeight);
          compositeCtx.fillStyle = '#ffffff';
          compositeCtx.fillRect(0, 0, outputWidth, outputHeight);
          compositeCtx.drawImage(gifAccumCanvas, scaledImgX, scaledImgY, scaledImgWidth, scaledImgHeight);

          // 그 위에 구멍 뚫린 템플릿 덮어씌우기 (스케일 적용)
          compositeCtx.drawImage(baseCanvas, 0, 0, baseCanvas.width, baseCanvas.height, 0, 0, outputWidth, outputHeight);

          // 합성 결과를 프레임으로 추가
          const delay = frame.delay || 100;
          gif.addFrame(compositeCtx, {
            delay,
            copy: true,
          });
        }

        // 6. GIF 렌더링 & 다운로드
        gif.on('finished', (blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `card-${cardId}-animated.gif`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        });

        gif.render();
      } catch (error) {
        console.error('Error downloading GIF card:', error);
        alert('GIF 카드 다운로드 중 오류가 발생했습니다: ' + error.message);
      }
    } else {
      // 기본 PNG 다운로드
      if (cardElement) {
        await downloadCard(cardElement, `card-${cardId}.png`);
      }
    }
  };

  // const handleDownloadAll = async () => {
  //   try {
  //     for (let i = 0; i < filteredCards.length; i++) {
  //       const cardElement = cardRefs.current[i];
  //       if (cardElement) {
  //         await downloadCard(cardElement, `card-${filteredCards[i].card_id}.png`);
  //         if (i < filteredCards.length - 1) {
  //           await new Promise(resolve => setTimeout(resolve, 500));
  //         }
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Error downloading all cards:', error);
  //     alert('일괄 다운로드 중 오류가 발생했습니다.');
  //   }
  // };

  const handleDownloadAll = async () => {
    try {
      const zip = new JSZip();
      
      for (let i = 0; i < filteredCards.length; i++) {
        const cardElement = cardRefs.current[i];
        if (cardElement) {
          const canvas = await html2canvas(cardElement, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            imageTimeout: 0,
          });
          
          const imageData = canvas.toDataURL('image/png').split(',')[1];
          zip.file(`card-${filteredCards[i].card_id}.png`, imageData, {base64: true});
        }
      }
      
      const zipContent = await zip.generateAsync({type: 'blob'});
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipContent);
      link.download = 'cardnews.zip';
      link.click();
      
    } catch (error) {
      console.error('Error downloading cards:', error);
      alert('일괄 다운로드 중 오류가 발생했습니다.');
    }
  };

  const getSecureImageUrl = (url) => {
    if (!url) return '';

    // HTTP URL을 백엔드 API를 통해 제공
    if (url.startsWith('http://')) {
      try {
        const urlObj = new URL(url);
        // 백엔드 서버의 경로만 추출하여 API baseURL과 결합
        const protocol = window.location.protocol;
        const hostname = window.location.hostname;

        // 로컬 개발환경과 프로덕션 환경 구분
        if (hostname === 'localhost') {
          return `${protocol}//${hostname}:22500${urlObj.pathname}`;
        } else {
          return `${protocol}//${hostname}/persona-content-gen-api${urlObj.pathname}`;
        }
      } catch (e) {
        return url;
      }
    }

    return url;
  };


  if (loading) return <div className="dashboard-loading">로딩 중...</div>;
  if (error) return <div className="dashboard-error">{error}</div>;

  return (
    <div className="dashboard-container">
      {/* 헤더 */}
      <div className='wrap-side'>
        <header>
          <h1 className="dashboard-title">카드뉴스 대시보드</h1>
          <div className="download-controls mb-4">
            <button onClick={handleDownloadAll} className="download-all-button">
              선택된 카드 일괄 다운로드 ({filteredCards.length}개)
            </button>
          </div>
          </header>

        {/* 좌측 필터(개선) */}
        <aside className="filter-sidebar">
          <div className="filter-section">
            {/* 검색창 */}
            <div className="search-section">
              <input
                type="text"
                placeholder="제목, 내용으로 검색..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSearchQuery(searchInput);
                  }
                }}
                className="search-input"
              />
              <button
                onClick={() => setSearchQuery(searchInput)}
                className="search-button"
                aria-label="검색"
              >
                검색
              </button>
              {searchInput && (
                <button
                  onClick={() => {
                    setSearchInput('');
                    setSearchQuery('');
                  }}
                  className="search-clear-button"
                  aria-label="검색어 지우기"
                >
                  ✕
                </button>
              )}
            </div>

            <FilterSection
              title="시나리오"
              options={filterOptions.scenarios}
              currentValue={filters.scenario}
              onSelect={(value) => setFilters({...filters, scenario: value})}
            />
            
            <FilterSection
              title="연령대"
              options={filterOptions.ages}
              currentValue={filters.age}
              onSelect={(value) => setFilters({...filters, age: value})}
            />
            
            <FilterSection
              title="성별"
              options={filterOptions.genders}
              currentValue={filters.gender}
              onSelect={(value) => setFilters({...filters, gender: value})}
            />
            
            <FilterSection
              title="생성일자"
              options={filterOptions.months}
              currentValue={filters.month}
              onSelect={(value) => setFilters({...filters, month: value})}
            />
            
            <FilterSection
              title="상태"
              options={filterOptions.statuses}
              currentValue={filters.status}
              onSelect={(value) => setFilters({...filters, status: value})}
            />
          </div>
        </aside>
      </div>
      {/* 카드 다운로드 */}
      {/* <div className="download-controls mb-4">
        <button 
          onClick={handleDownloadAll}
          className="download-all-button"
        >
          선택된 카드 일괄 다운로드 ({filteredCards.length}개)
        </button>
      </div> */}

      {/* 메인 컨텐츠 영역 - 기존 카드뉴스 부분 유지 */}
      <main className="main-content">


        {/* 카드뉴스 영역 */}
        <div className="cards-grid">
        {filteredCards.map((card, index) => (
          <div key={card.card_id} className="card-news-container">

            {/* 카드 id, 상태 */}
            <div className="badge-container">
              <div className="card-id-badge">
                  카드 ID: {card.card_id}
                </div>

                {/* New text overflow indicator */}
                {isTextOverflowing(card.description) && (
                  <div className="text-overflow-indicator">
                    글자수 초과
                  </div>
                )}
                <div className={`status-badge ${card.status}`}>
                  {card.status}
                </div>
            </div>

            {/* 카드뉴스 이미지 영역(다운로드가능) */}
            <div
              className="card-news-content"
              ref={el => cardRefs.current[index] = el}
              style={(() => {
                // 수정 모드일 때는 editData 사용, 아니면 card 데이터 사용
                const bgTemplate = editingId === card.card_id
                  ? editData.bg_url
                  : card.bg_url;
                if (bgTemplate && bgTemplate !== 'none') {
                  // 파일명만 있으면 서버 경로 추가
                  const bgUrl = bgTemplate.startsWith('/') || bgTemplate.startsWith('http')
                    ? bgTemplate
                    : `/img_card_background/${bgTemplate}`;
                  return {
                    backgroundImage: `linear-gradient(rgba(255,255,255,0.5), rgba(255,255,255,0.5)), url(${bgUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  };
                }
                return {};
              })()}
            >
              <div className="card-text">
                {editingId === card.card_id ? (
                  <div className="edit-form">
                    <span>제목</span>
                    <input
                      type="text"
                      value={editData.title}
                      onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                      className="edit-input title-input"
                      placeholder="제목"
                    />
                    <span>부제목</span>
                    <input
                      type="text"
                      value={editData.subtitle}
                      onChange={(e) => setEditData({ ...editData, subtitle: e.target.value })}
                      className="edit-input subtitle-input"
                      placeholder="부제목"
                    />
                    <span>설명</span>
                    <textarea
                      value={editData.description}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      className="edit-input description-input"
                      placeholder="설명"
                    />
                    <span>심의번호</span>
                    <input
                      type="text"
                      value={editData.approval_number}
                      onChange={(e) => setEditData({ ...editData, approval_number: e.target.value })}
                      className="edit-input approval-input"
                      placeholder="심의번호"
                    />
                    <div className="font-selector">
                      <span className="font-label">폰트</span>
                      <select
                        value={editData.font_style}
                        onChange={(e) => setEditData({ ...editData, font_style: e.target.value })}
                        className="font-select"
                      >
                        {fontOptions.map((font) => (
                          <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                            {font.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="background-selector">
                      <span className="background-label">배경</span>
                      <select
                        value={editData.bg_url}
                        onChange={(e) => setEditData({ ...editData, bg_url: e.target.value })}
                        className="background-select"
                      >
                        {backgroundOptions.map((bg) => (
                          <option key={bg.value} value={bg.value}>
                            {bg.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="status-toggle-group">
                      <span className="status-label">상태</span>
                      <div className="status-buttons">
                        {statusOptions.map((status) => (
                          <button
                            key={status}
                            className={`status-button ${editData.status === status ? 'active' : ''}`}
                            onClick={() => handleStatusChange(status)}
                            type="button"
                          >
                            {status}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="image-shape-selector">
                      <span className="shape-label">이미지 형태</span>
                      <div className="shape-buttons">
                        <button
                          type="button"
                          className={`shape-button ${editData.border_radius === '150px' ? 'active' : ''}`}
                          onClick={() => setEditData({ ...editData, border_radius: '150px' })}
                        >
                          원형
                        </button>
                        <button
                          type="button"
                          className={`shape-button ${editData.border_radius === '15px' ? 'active' : ''}`}
                          onClick={() => setEditData({ ...editData, border_radius: '15px' })}
                        >
                          사각형
                        </button>
                      </div>
                    </div>
                    <div className="image-regenerate-section">
                      <button
                        type="button"
                        className={`regenerate-single-button ${singleRegenerating[card.card_id] ? 'loading' : ''}`}
                        onClick={() => handleSingleRegenerateImage(card.card_id)}
                        disabled={singleRegenerating[card.card_id]}
                      >
                        {singleRegenerating[card.card_id] ? (
                          <>
                            <span className="spinner"></span>
                            생성 중...
                          </>
                        ) : (
                          '이미지 재생성'
                        )}
                      </button>
                    </div>
                    <div className="edit-buttons">
                      <button className="save-button" onClick={() => handleUpdate(card.card_id)}>
                        저장
                      </button>
                      <button className="cancel-button" onClick={handleCancelEdit}>
                        취소
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="card-title-db" style={getFontStyle(card.font_style, 'title')}>{card.title}</div>
                    <div className="card-subtitle-db" style={getFontStyle(card.font_style, 'subtitle')}>{card.subtitle}</div>
                    <div className="card-image-db">
                      <img
                        src={getSecureImageUrl(
                          activeTab[card.card_id] === 'gif' && card.gif_url
                            ? card.gif_url
                            : card.img_url
                        )}
                        alt={card.title}
                        className="generated-image"
                        style={{ borderRadius: card.border_radius || '15px' }}
                        crossOrigin="anonymous"
                      />
                    </div>
                    <div className="card-description-db-wrap">
                      <p className="card-description-db" style={getFontStyle(card.font_style, 'description')}>
                        {card.description}
                      </p>
                      {isTextOverflowing(card.description) && (
                        <div className="image-overflow">
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              {editingId !== card.card_id && (
                <CardFooter approvalNumber={card.approval_number} />
              )}
            </div>
            {editingId !== card.card_id && (
              <div className="card-controls">
                  {editingId !== card.card_id ? (
                    <>
                      <button className="edit-button" onClick={() => handleEdit(card)}>
                        수정
                      </button>
                      <button
                        className="download-button"
                        onClick={() => handleDownloadSingle(card.card_id, index)}
                      >
                        다운
                      </button>

                      {/* gif_url이 없으면 GIF 생성 버튼 표시 */}
                      {!card.gif_url && (
                        <button
                          className={`gif-button ${gifGenerating[card.card_id] ? 'loading' : ''} ${gifSuccess[card.card_id] ? 'success' : ''}`}
                          onClick={() => handleGifMode(card.card_id)}
                          disabled={gifGenerating[card.card_id] || gifSuccess[card.card_id]}
                        >
                          {gifSuccess[card.card_id] ? (
                            <>
                              <svg className="button-checkmark-svg" viewBox="0 0 52 52">
                                <circle className="button-checkmark-circle" cx="26" cy="26" r="25" fill="none"/>
                                <path className="button-checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
                              </svg>
                              완료
                            </>
                          ) : gifGenerating[card.card_id] ? (
                            <>
                              <span className="spinner"></span>
                              {gifQueuePosition[card.card_id] > 0 ? (
                                `대기중 (${gifQueuePosition[card.card_id]}번째)`
                              ) : gifQueuePosition[card.card_id] === 0 ? (
                                '생성 중...'
                              ) : (
                                '생성 중...'
                              )}
                            </>
                          ) : (
                            'GIF 생성'
                          )}
                        </button>
                      )}

                      {/* gif_url이 있으면 토글 스위치 표시 */}
                      {card.gif_url && (
                        <div className="toggle-container">
                          <span className="toggle-label">기본</span>
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={activeTab[card.card_id] === 'gif'}
                              onChange={() => handleTabChange(card.card_id, activeTab[card.card_id] === 'gif' ? 'normal' : 'gif')}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                          <span className="toggle-label">GIF</span>
                        </div>
                      )}
                    </>
                  ) : null}
                </div>


            )}
          </div>
        ))}
      </div>

      {/* 페이지네이션 */}
      {pagination.total_pages > 1 && (
        <div className="pagination-container">
          <div className="pagination">
            {/* 이전 페이지 버튼 */}
            <button
              className={`pagination-btn ${!pagination.has_prev ? 'disabled' : ''}`}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pagination.has_prev}
            >
              이전
            </button>

            {/* 페이지 번호들 */}
            {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map(page => {
              // 현재 페이지 주변 5개만 표시
              const shouldShow =
                page === 1 ||
                page === pagination.total_pages ||
                (page >= currentPage - 2 && page <= currentPage + 2);

              if (!shouldShow) {
                // ... 표시를 위한 조건
                if (page === currentPage - 3 || page === currentPage + 3) {
                  return <span key={page} className="pagination-dots">...</span>;
                }
                return null;
              }

              return (
                <button
                  key={page}
                  className={`pagination-btn ${page === currentPage ? 'active' : ''}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              );
            })}

            {/* 다음 페이지 버튼 */}
            <button
              className={`pagination-btn ${!pagination.has_next ? 'disabled' : ''}`}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.has_next}
            >
              다음
            </button>
          </div>

          {/* 페이지 정보 표시 */}
          <div className="pagination-info">
            총 {pagination.total}개의 카드 | {currentPage} / {pagination.total_pages} 페이지
          </div>
        </div>
      )}
      </main>

      {/* 이미지 재생성 모달창 */}
      <ImageSelectionModal
      isOpen={isImageModalOpen}
      onClose={handleCancelVariation}
      currentImage={currentImageUrl}
      variations={imageVariations}
      selectedVariation={selectedVariation}
      onSelect={handleSelectVariation}
      onApply={handleApplyVariation}
      cardId={activeCardId}
      cardInfo={currentCardInfo}  // 카드 정보 전달
    />

    </div>
  );

};

export default CardDashboard;